import CancelOutlined from '@mui/icons-material/CancelOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useRef } from 'react';

import actionsColumn from '~components/DataTable/actionsColumn';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { RUTTER_PLATFORM_QUICK_BOOKS, RutterConnection } from '~hooks/useRutter/models';

export type RutterManagementRow = {
  id: string;
  isDeleting?: boolean;
  connection: RutterConnection;
  onDelete: () => void;
};

export const platformColDef: DataTableColumn<RutterManagementRow> = {
  field: 'platform',
  headerName: t('administration.integration.rutter.grid.platform'),
  valueGetter: (row) => {
    const { connection } = row;
    if (!connection.platform) {
      return t('administration.integration.rutter.grid.pending_link');
    }
    if (connection.isQuickBooksDesktop) {
      return t('administration.integration.rutter.grid.quick_books_desktop');
    }
    if (connection.platform === RUTTER_PLATFORM_QUICK_BOOKS) {
      return t('administration.integration.rutter.grid.quick_books_online');
    }
    return connection.platform;
  },
};

export const statusColDef: DataTableColumn<RutterManagementRow> = {
  field: 'status',
  headerName: t('administration.integration.rutter.grid.status'),
  renderCell: (row) => {
    return (
      <Chip
        size="small"
        color={row.connection.isConnected ? 'success' : 'default'}
        label={
          row.connection.status ??
          t('administration.integration.rutter.grid.pending_link')
        }
        variant="outlined"
      />
    );
  },
};

export const lastSyncColDef: DataTableColumn<RutterManagementRow> = {
  field: 'lastSync',
  headerName: t('administration.integration.rutter.grid.last_sync'),
  valueGetter: (row) =>
    row.connection.synchronizedAt
      ? dayjs.tz(row.connection.synchronizedAt).format("h:mm A ddd MMM-DD 'YY")
      : '-',
};

function RutterConnectionActionsCell({ row }: { row: RutterManagementRow }) {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Menu
        ref={menuHandler}
        menuTrigger={
          <SmallButton
            color="brandV2TreadGray3"
            variant="text"
            startIcon={<MoreHoriz />}
            sx={{
              '&.MuiButtonBase-root': {
                border: 'solid 1px transparent',
                borderRadius: '50%',
                height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                p: 0,
                width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                '.MuiButton-startIcon': { mx: 0 },
              },
            }}
          />
        }
      >
        <MenuItem
          key={'Delete'}
          onClick={(event) => {
            event.stopPropagation();
            row.onDelete();
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              gap: 1,
            }}
          >
            <CancelOutlined
              sx={{ color: theme.brandV2.colors.treadBlack, fontSize: '16px' }}
            />

            <Typography color={theme.brandV2.colors.treadBlack} variant={'body2'}>
              {t('administration.integration.rutter.cta.disconnect')}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export const actionsColDef: DataTableColumn<RutterManagementRow> = actionsColumn(
  (row) => <RutterConnectionActionsCell row={row} />,
);
